// React
import { useState, useEffect, useContext } from "react";

// Api endpoints, hooks, and utility functions
import { updateApplication } from "../../../api/endpoints/application";

import UpdateApplication from "./UpdateApplication/UpdateApplication";
import { useTranslation } from "react-i18next";
import useTimeAgo from "../../hooks/useTimeAgo";

// Assets
import anonymousAvatar from "../../../assets/icons/anonymous-avatar.svg";

// Components
import { CurriculumVitae, Icon } from "../../../components";
import { Col, Grid, Row } from "../../../components/Grid";
import { Typography } from "../../../components/Typography";
import { CheckboxWidthLabel } from "../../../components/Input/Input";
import { ApplicationWrapper, ApplicationTopBar, ApplicantMeta, ApplicantHeadings, ApplicantAvatar, InterviewReminderToggle } from "./Application.styled";
import ApplicationStars from "./ApplicationStars/ApplicationStars";
import { ApplicantContactInfo } from "./ApplicantContactInfo/ApplicantContactInfo";
import StatusSelector from "./StatusSelector/StatusSelector";
import CVRating from "../../../components/CurriculumVitae/CVRating";
import ApplicationActionModal from "../../components/ApplicationCard/ApplicationActionModal";
import { UpdateLoading } from "../../../components/Loader/style";
import { ShimmerText } from "../../components/Shimmer/Shimmer";

// State management
import { StatePositionContext, DispatchPositionContext } from "../../context/PositionContext";
import { useKanbanState, useKanbanDispatch } from "../../context/KanbanContext";
import { ApplicationParamsContext } from "../../components/ApplicationList/ApplicationContextWrapper";

export const Application = ({ selectedApplicationId, isVisible, manageShareholder }) => {
  const { t } = useTranslation();
  const [confirmation, setConfirmation] = useState(false);
  const positionState = useContext(StatePositionContext);
  const positionDispatch = useContext(DispatchPositionContext);
  const applicationParamsContext = useContext(ApplicationParamsContext);
  const shareholderApplications = applicationParamsContext?.state?.list || [];
  const shareholderIndex = applicationParamsContext?.state?.index ?? 0;
  const applicationListDispatch = applicationParamsContext?.dispatch;
  const isAnonymous = positionState?.selectedApplication?.position?.anon;
  const candidateInitials = `${positionState?.selectedApplication?.applicant?.candidate?.firstName?.charAt(0).toUpperCase() || ""}${positionState?.selectedApplication?.applicant?.candidate?.lastName?.charAt(0).toUpperCase() || ""}`;
  const { refetchCompactApplications } = useKanbanDispatch();
  const { compactApplications } = useKanbanState();
  const [numberOfApplications, setNumberOfApplications] = useState("");
  const [modalAction, setModalAction] = useState(null);

  // Format days weeks month
  const { long: timeAgo } = useTimeAgo(positionState?.selectedApplication?.createdAt);

  useEffect(() => {
    if (manageShareholder) {
      const count = shareholderApplications.length;
      const currentIndex = count > 0 ? shareholderIndex + 1 : 0;

      setNumberOfApplications(count > 0 ? `${currentIndex} ${t("common.of", "of")} ${count}` : "");
      return;
    }

    // Set number of applications in the same substatus for the current application
    if (positionState?.selectedApplication && compactApplications) {
      const currentColumnKey = Object.keys(compactApplications).find((key) => compactApplications[key].some((app) => app.id === positionState.selectedApplication.id));
      const applicationsInSubstatus = currentColumnKey ? compactApplications[currentColumnKey] : [];
      const count = applicationsInSubstatus.length;
      const currentIndex = applicationsInSubstatus.findIndex((app) => app.id === positionState.selectedApplication.id);

      setNumberOfApplications(currentIndex > -1 ? `${currentIndex + 1} ${t("common.of", "of")} ${count}` : `0 ${t("common.of", "of")} ${count}`);
    } else {
      setNumberOfApplications("");
    }
  }, [manageShareholder, shareholderApplications, shareholderIndex, positionState, compactApplications]);

  // useEffect(() => {
  //   console.log("selectedApplication:", positionState?.selectedApplication);
  // }, [positionState]);

  // useEffect(() => {
  //   if (positionState.selectedApplication) {
  //     if (positionState.selectedApplication.externalProfileImage) {
  //       setAvatar(positionState.selectedApplication.externalProfileImage);
  //       return;
  //     }

  //     const { files } = positionState.selectedApplication;

  //     if (files) {
  //       const profileImage = files.find(
  //         (fileObject) => fileObject.docType === "profileImage",
  //       );

  //       if (profileImage) {
  //         setAvatar(profileImage.signedRequest);
  //       } else {
  //         setAvatar(anonymousAvatar);
  //       }
  //     }
  //   }
  // }, [positionState]);

  const updateParentState = (newState) => {
    setConfirmation(newState);
  };

  const handleDropdownChange = async (application, selectedOption) => {
    // Figure out if we need to go to next or stay in current substatus
    // Go to next if more applications in the same substatus else go to the updated application substatus
    const currentApplication = positionState?.selectedApplication;

    if (!currentApplication?.id) {
      refetchCompactApplications();
      return;
    }

    const applicationsInCurrentSubstatus = Object.values(compactApplications || {})
      .flat()
      .filter((app) => app.substatus === currentApplication.substatus);

    const currentIndex = applicationsInCurrentSubstatus.findIndex((app) => app.id === currentApplication.id);

    const hasOtherApplicationsInSubstatus = applicationsInCurrentSubstatus.length > 1;

    let nextSelectedApplication = null;

    if (currentIndex > -1 && hasOtherApplicationsInSubstatus) {
      const nextIndex = currentIndex === applicationsInCurrentSubstatus.length - 1 ? currentIndex - 1 : currentIndex + 1;
      nextSelectedApplication = applicationsInCurrentSubstatus[nextIndex];
    }

    await positionDispatch({
      type: "fetch_selected_application",
      payload: nextSelectedApplication
        ? {
            id: nextSelectedApplication.id,
            substatus: nextSelectedApplication.substatus,
          }
        : {
            id: currentApplication.id,
            substatus: selectedOption?.value ?? application?.substatus ?? currentApplication.substatus,
          },
    });

    refetchCompactApplications();
  };

  const handlePrev = () => {
    if (manageShareholder) {
      applicationListDispatch?.({ type: "dec_index" });
      return;
    }

    positionDispatch({
      type: "select_previous_application",
      payload: { compactApplications },
    });
  };

  const handleNext = () => {
    if (manageShareholder) {
      applicationListDispatch?.({ type: "inc_index" });
      return;
    }

    positionDispatch({
      type: "select_next_application",
      payload: { compactApplications },
    });
  };

  const handleActionClick = (actionName) => {
    setModalAction(actionName);
  };

  const handleInterviewedToggle = async () => {
    const currentApplication = positionState?.selectedApplication;
    const applicationId = currentApplication?.id;

    if (!applicationId || manageShareholder) {
      return;
    }

    const previousInterviewed = currentApplication?.interviewed ?? false;
    const interviewed = !previousInterviewed;

    positionDispatch({
      type: "update_selected_application",
      payload: { id: applicationId, interviewed },
    });

    try {
      const response = await updateApplication({
        selector: applicationId,
        body: { interviewed },
      });

      if (!response?.success) {
        positionDispatch({
          type: "update_selected_application",
          payload: { id: applicationId, interviewed: previousInterviewed },
        });
        return;
      }
    } catch (error) {
      positionDispatch({
        type: "update_selected_application",
        payload: { id: applicationId, interviewed: previousInterviewed },
      });
    }
  };

  const showLoadingState = positionState.candidateView && positionState.isLoadingSelectedApplication;
  const binaryChoiceAnswers = positionState?.selectedApplication?.binaryChoices || {};

  return (
    <>
      {confirmation && <UpdateLoading success>{t("common.emailCopied", "E-mail copied to clipboard")}</UpdateLoading>}

      {modalAction && (
        <ApplicationActionModal
          setModalAction={setModalAction}
          modalAction={modalAction}
          applications={[positionState.selectedApplication]}
          onSuccess={() => {
            handleNext();
            refetchCompactApplications();
          }}
        />
      )}
      <ApplicationWrapper applicantView={positionState.candidateView} manageShareholder={manageShareholder}>
        <>
          <ApplicationTopBar>
            <>
              <div>
                <Icon
                  icon="arrow_left"
                  mr="24"
                  hideOnDesktop
                  pointer
                  onClick={() =>
                    positionDispatch({
                      type: "candidate_view",
                      payload: false,
                    })
                  }
                />
                {showLoadingState ? (
                  <ShimmerText words={1} tag="p" nomargin large color="#52576A" />
                ) : (
                  <Typography tag="p" nomargin large color="#52576A" ml="16">
                    {timeAgo}
                  </Typography>
                )}
              </div>
              <div>
                <Typography tag="p" nomargin large color="#52576A">
                  {numberOfApplications}
                </Typography>
                <Icon icon="next_prev" onClick={handlePrev} ml="24" pointer />
                <Icon icon="next_prev" onClick={handleNext} ml="24" pointer scale="-1" />
                {!manageShareholder && (
                  <Icon
                    icon="collapse"
                    scale="-1"
                    ml="48"
                    hideOnMobile
                    pointer
                    onClick={() =>
                      positionDispatch({
                        type: "candidate_view",
                        payload: false,
                      })
                    }
                  />
                )}
              </div>
            </>
          </ApplicationTopBar>
          <Grid fluid flex heightSM={manageShareholder ? "100%" : "calc(100% - 70px - 64px);"} heightMD={manageShareholder ? "100%" : "calc(100% - 273px - 40px);"}>
            <Row
              height="100%"
              width="250px"
              flexDirectionMD="column"
              plMD="44"
              prMD="44"
              ptMD="26"
              bg="rgba(243, 243, 245, 0.6);"
              ml="0"
              mr="0"
              hideOnMobile
              applicationInfo
              $loading={showLoadingState}
            >
              <ApplicantAvatar large avatar={anonymousAvatar} isAnonymous={isAnonymous}>
                {!isAnonymous && (
                  <Typography tag="p" fontSize="48" mb="0" bold light>
                    {candidateInitials}
                  </Typography>
                )}
              </ApplicantAvatar>
              <ApplicantContactInfo single={positionState?.selectedApplication} isAnonymous={isAnonymous} loading={showLoadingState} onUpdateState={updateParentState} />
              {Object.keys(positionState?.selectedApplication?.competencies || {}).length > 0 && (
                <>
                  <Typography tag="p" bold fontWeightLG="600" nomargin mb="10" mt="15">
                    {t("common.skills", "Skills")}
                  </Typography>
                  <CVRating single={positionState?.selectedApplication?.competencies} />
                </>
              )}
              {Object.keys(positionState?.selectedApplication?.languages || {}).length > 0 && (
                <>
                  <Typography tag="p" bold fontWeightLG="600" nomargin mb="10" mt="15">
                    {t("common.languages", "Languages")}
                  </Typography>
                  <CVRating single={positionState?.selectedApplication?.languages} />
                </>
              )}
              {Object.keys(binaryChoiceAnswers).length > 0 && (
                <>
                  <Typography tag="p" bold fontWeightLG="600" nomargin mb="10" mt="15">
                    {t("common.binaryChoices", "Binary choices")}
                  </Typography>
                  {Object.entries(binaryChoiceAnswers).map(([question, answer]) => (
                    <div key={question} className="rating">
                      <Typography tag="p" nomargin>
                        {question}
                      </Typography>
                      <Typography tag="p" nomargin mb="10" fontWeight="600" red>
                        {answer === true ? t("common.yes", "Yes") : answer === false ? t("common.no", "No") : "-"}
                      </Typography>
                    </div>
                  ))}
                </>
              )}
            </Row>
            <Row ml="0" mr="0" pb="50" height="100%" widthMD="calc(100% - 250px)" cv>
              <Col mlMD="26" mrMD="26">
                <ApplicantMeta>
                  <ApplicantAvatar large avatar={anonymousAvatar} isAnonymous={isAnonymous} mb="30" hideOnDesktop>
                    {!isAnonymous && (
                      <Typography tag="p" fontSize="48" mb="0" bold light>
                        {candidateInitials}
                      </Typography>
                    )}
                  </ApplicantAvatar>
                  <ApplicantHeadings>
                    {showLoadingState ? (
                      <ShimmerText words={3} mbLG="10" tag="p" small nomargin mb="10" fontSize="14" />
                    ) : (
                      <Typography mbLG="10" fontWeightLG="300" tag="p" small uppercase bold lowOpacity letterSpaced nomargin mb="10" fontSize="14">
                        {positionState?.selectedApplication?.position?.title}
                      </Typography>
                    )}
                    {showLoadingState ? (
                      <ShimmerText words={2} tag="h1" mb="20" />
                    ) : (
                      <Typography tag="h1" bold capitalize mb="20" isAnonymous={isAnonymous}>
                        {positionState?.selectedApplication?.applicant?.candidate?.firstName + " " + positionState?.selectedApplication?.applicant?.candidate?.lastName}
                      </Typography>
                    )}
                    <ApplicationStars id={positionState?.selectedApplication} loading={showLoadingState} disabled={manageShareholder} />
                  </ApplicantHeadings>
                  {/* {positionState?.selectedApplication?.status !== "invited" && positionState?.selectedApplication?.status !== "rejected" && ( */}
                  {positionState?.selectedApplication?.status !== "rejected" && (
                    <StatusSelector
                      application={positionState?.selectedApplication}
                      loading={showLoadingState}
                      hideOnMobile
                      handleChange={handleDropdownChange}
                      manageShareholder={manageShareholder}
                    />
                  )}
                  {positionState?.selectedApplication?.status !== "invited" && !manageShareholder && (
                    <InterviewReminderToggle>
                      {showLoadingState ? (
                        <ShimmerText words={2} tag="p" mb="20" />
                      ) : (
                        <CheckboxWidthLabel
                          type="checkbox"
                          id="interviewed-reminder"
                          label={t("common.interviewReminderToggle", "Mark as interviewed")}
                          checked={Boolean(positionState?.selectedApplication?.interviewed)}
                          name="interviewed-reminder"
                          onChange={handleInterviewedToggle}
                          disabled={showLoadingState || manageShareholder}
                          red
                        />
                      )}
                    </InterviewReminderToggle>
                  )}
                </ApplicantMeta>
                <ApplicantContactInfo single={positionState?.selectedApplication} isAnonymous={isAnonymous} loading={showLoadingState} hideOnDesktop onUpdateState={updateParentState} />
                {showLoadingState ? <ShimmerText long tag="p" fontSize="16" mb="10" mt="30" /> : <CurriculumVitae data={positionState?.selectedApplication} />}
              </Col>
            </Row>
          </Grid>
          <UpdateApplication
            single={positionState?.selectedApplication}
            loadingApplication={showLoadingState}
            onSubstatusChange={handleDropdownChange}
            manageShareholder={manageShareholder}
            handleActionClick={handleActionClick}
          />
        </>
      </ApplicationWrapper>
    </>
  );
};

export default Application;
